import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { AccountInfo, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInternal, msalExternal, internalScopes, externalScopes } from "./msalConfig";
import { configureTokenProvider } from "../apiClient";

type AuthType = "internal" | "external" | null;

interface AuthContextValue {
  authType: AuthType;
  account: AccountInfo | null;
  roles: string[];
  ssoChecking: boolean;
  loginInternal: () => Promise<void>;
  loginExternal: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authType, setAuthType] = useState<AuthType>(
    () => localStorage.getItem("authType") as AuthType
  );
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [ssoChecking, setSsoChecking] = useState(true);

  useEffect(() => {
    // Guard against React StrictMode double-invocation in development,
    // which would fire two simultaneous ssoSilent iframe requests.
    let cancelled = false;

    const restoreType = localStorage.getItem("authType") as AuthType;

    const initAuth = async () => {
      // MSAL v5 uses BroadcastChannel for ssoSilent: the hidden iframe loads this
      // same app, so we must broadcast the token response back to the main frame
      // instead of re-running the full auth flow (which MSAL would block anyway).
      if (window.parent !== window) {
        // Auth response already broadcast to parent frame by the inline script
        // in index.html (runs before React bundles load). Nothing else to do.
        if (!cancelled) setSsoChecking(false);
        return;
      }

      await Promise.all([msalInternal.initialize(), msalExternal.initialize()]);

      const [internalResult, externalResult] = await Promise.all([
        msalInternal.handleRedirectPromise(),
        msalExternal.handleRedirectPromise(),
      ]);

      if (cancelled) return;

      if (internalResult?.account) {
        setAccount(internalResult.account);
        setAuthType("internal");
        localStorage.setItem("authType", "internal");
        setSsoChecking(false);
        return;
      }
      if (externalResult?.account) {
        setAccount(externalResult.account);
        setAuthType("external");
        localStorage.setItem("authType", "external");
        setSsoChecking(false);
        return;
      }

      // Restore from MSAL cache on page refresh (fast, no network)
      if (restoreType === "internal") {
        const cached = msalInternal.getAllAccounts();
        if (cached.length > 0 && !cancelled) {
          setAccount(cached[0]);
          setSsoChecking(false);
          return;
        }
      }
      if (restoreType === "external") {
        const cached = msalExternal.getAllAccounts();
        if (cached.length > 0 && !cancelled) {
          setAccount(cached[0]);
          setSsoChecking(false);
          return;
        }
      }

      // No cached session — attempt silent SSO against the internal M365 tenant.
      // domainHint avoids an account-discovery round-trip.
      // redirectUri points to blank.html so the iframe doesn't reload the app.
      // No custom timeout — MSAL's own iframeHashTimeout handles the deadline.
      try {
        // Use only OIDC scopes here — requesting API scopes can fail with
        // consent_required when the user hasn't logged in through this app yet.
        // API tokens are acquired on-demand via acquireTokenSilent later.
        const ssoResult = await msalInternal.ssoSilent({
          scopes: ["openid", "profile"],
          domainHint: process.env.REACT_APP_INTERNAL_DOMAIN,
        });
        if (cancelled) return;
        setAccount(ssoResult.account);
        setAuthType("internal");
        localStorage.setItem("authType", "internal");
      } catch (err) {
        console.warn("[SSO] Silent SSO failed:", err);
      } finally {
        if (!cancelled) setSsoChecking(false);
      }
    };

    initAuth();

    return () => { cancelled = true; };
  }, []);

  const loginInternal = async () => {
    await msalInternal.initialize();
    await msalInternal.loginRedirect({ scopes: internalScopes });
  };

  const loginExternal = async () => {
    await msalExternal.initialize();
    await msalExternal.loginRedirect({ scopes: externalScopes });
  };

  const logout = () => {
    const instance = authType === "internal" ? msalInternal : msalExternal;
    localStorage.removeItem("authType");
    instance.logoutRedirect();
  };

  const getAccessToken = useCallback(async (): Promise<string> => {
    const instance = authType === "internal" ? msalInternal : msalExternal;
    const scopes = authType === "internal" ? internalScopes : externalScopes;
    const accounts = instance.getAllAccounts();
    if (!accounts.length) throw new Error("No authenticated account");

    try {
      const result = await instance.acquireTokenSilent({
        scopes,
        account: accounts[0],
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // The AAD server-side session no longer exists (expired, revoked, or
        // invalidated). Fall back to redirect-based acquisition — it will
        // renew the session and return to the current page.
        await instance.acquireTokenRedirect({ scopes, account: accounts[0] });
        return ''; // never reached — redirect navigates away
      }
      throw err;
    }
  }, [authType]);

  useEffect(() => {
    if (!ssoChecking) {
      configureTokenProvider(getAccessToken);
    }
  }, [getAccessToken, ssoChecking]);

  // Roles live in the access token (aud = API), not the ID token stored in account.idTokenClaims.
  // Decode the JWT payload without verification — enforcement is on the server side.
  useEffect(() => {
    if (ssoChecking || !authType) {
      setRoles([]);
      return;
    }
    getAccessToken().then(token => {
      try {
        const segment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const decoded = JSON.parse(atob(segment));
        setRoles(Array.isArray(decoded.roles) ? decoded.roles : []);
      } catch {
        setRoles([]);
      }
    }).catch(() => setRoles([]));
  }, [ssoChecking, authType, getAccessToken]);

  return (
    <AuthContext.Provider value={{
      authType, account, roles, ssoChecking, loginInternal, loginExternal, logout, getAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
